import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges, OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';

import { AccountService } from '../../../../services/account.service';
import { LogService } from '../../../../services/log.service';
import {
  AccountDto,
  AccountRole,
  SerializedAccountDto,
  UpdateAccountDto,
  UpdateAccountResponseDto,
} from '../../../../types/dtos/account.dto';
import { LoggingActionEnum, LoggingTargetEnum } from '../../../../types/enums/logging.enum';
import {
  InputSelectButtonInterface,
  InputSelectInterface,
  InputSubmitInterface,
  InputTextInterface,
} from '../../../../types/interfaces/input.interface';
import { InputSelectButton } from '../../input-select-button/input-select-button';
import { InputSelectComponent } from '../../input-select/input-select';
import { InputSubmitComponent } from '../../input-submit/input-submit.component';
import { InputTextComponent } from '../../input-text/input-text.component';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-dialog-edit-user',
  standalone: true,
  imports: [
    InputSubmitComponent,
    InputTextComponent,
    InputSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    InputSelectButton,
  ],
  templateUrl: './dialog-edit-user.html',
  styleUrl: './dialog-edit-user.css',
})
export class DialogEditUser implements OnInit, AfterViewInit, OnChanges {
  @Input()
  public account!: AccountDto | SerializedAccountDto | null;

  @Input()
  public isCurrentAccount: boolean = false;

  @Output()
  public updated: EventEmitter<void> = new EventEmitter<void>();
  public accountForm: FormGroup = new FormGroup({
    username: new FormControl({
      disabled: true,
    }),
    email: new FormControl(),
    full_name: new FormControl(),
    language: new FormControl('de'),
    role: new FormControl(),
    accountStatus: new FormControl(),
    currentPassword: new FormControl(),
    password: new FormControl(),
    passwordRepeat: new FormControl(),
  });
  public usernameConfig: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.username.label',
    isTranslated: true,
    id: 'username',
    type: 'text',
    iconLeft: 'person_book',
    readonly: true,
  };
  public changeEmail: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.email.label',
    isTranslated: true,
    id: 'email',
    type: 'text',
    iconLeft: 'alternate_email',
  };
  public changeFullName: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.full-name.label',
    isTranslated: true,
    id: 'full_name',
    type: 'text',
    iconLeft: 'id_card',
  };
  public currentPassword: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.current-password.label',
    isTranslated: true,
    id: 'currentPassword',
    type: 'password',
    visibility: true,
    iconLeft: 'key',
  };
  public changePassword: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.new-password.label',
    isTranslated: true,
    id: 'password',
    type: 'password',
    visibility: true,
    iconLeft: 'key',
  };
  public changePasswordRepeat: InputTextInterface = {
    formGroup: this.accountForm,
    label: 'common.input.confirm-password.label',
    isTranslated: true,
    id: 'passwordRepeat',
    type: 'password',
    visibility: true,
    iconLeft: 'key',
  };
  public submitConfig: InputSubmitInterface = {
    formGroup: this.accountForm,
    label: 'common.actions.save',
    isTranslated: true,
  };
  public languageSelect: InputSelectInterface = {
    formGroup: this.accountForm,
    label: 'common.input.language-select.label',
    isTranslated: true,
    id: 'language',
    iconLeft: 'language',
    required: true,
    options: [
      {
        isTranslated: true,
        label: 'common.input.language-select.values.german',
        value: 'de',
      },
      {
        isTranslated: true,
        label: 'common.input.language-select.values.english',
        value: 'en',
      },
      {
        isTranslated: true,
        label: 'common.input.language-select.values.french',
        value: 'fr',
      },
      {
        isTranslated: true,
        label: 'common.input.language-select.values.luxembourgish',
        value: 'lu',
      },
    ],
  };
  public accountStatusConfig: InputSelectButtonInterface = {
    formGroup: this.accountForm,
    label: 'common.fields.status',
    isTranslated: true,
    id: 'accountStatus',
    iconLeft: 'person',
    required: true,
    options: [
      {isTranslated: true, label: 'common.status.activated', value: 'active'},
      {isTranslated: true, label: 'common.status.deactivated', value: 'disabled'},
    ],
  };
  public currentAccount: SerializedAccountDto | null = null;
  private _updateAccount: UpdateAccountDto = {};
  private readonly BaseEditRoleOptions = [
    {label: 'common.roles.viewer', value: AccountRole.VIEWER, isTranslated: true},
    {label: 'common.roles.operator', value: AccountRole.OPERATOR, isTranslated: true},
    {label: 'common.roles.admin', value: AccountRole.ADMIN, isTranslated: true},
  ];
  public roleSelect: InputSelectInterface = {
    formGroup: this.accountForm,
    label: 'common.fields.role',
    isTranslated: true,
    id: 'role',
    iconLeft: 'shield_person',
    required: true,
    options: this.BaseEditRoleOptions,
  };
  private _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _messageService: MessageService,
    private readonly _accountService: AccountService,
    private readonly _translateService: TranslateService,
    private readonly _logService: LogService
  ) {
  }

  ngAfterViewInit(): void {
    this._logService.sendLog({
      target: LoggingTargetEnum.USER,
      target_id: this.currentAccount?.id,
      action: LoggingActionEnum.VIEW,
      description: 'Schaut Benutzer im edit-Modus an',
    });
  }

  public ngOnInit(): void {
    this._accountService.account$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((account: SerializedAccountDto | null) => {
        this.currentAccount = account;
        this.roleSelect.options = [...this.BaseEditRoleOptions];

        if (this.currentAccount?.role == AccountRole.SUPER_ADMIN) {
          this.roleSelect.options.push({
            label: 'common.roles.super-admin',
            value: AccountRole.SUPER_ADMIN,
            isTranslated: true
          });
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['account'] && this.account) {
      this.fillForm(this.account as AccountDto);
    }
  }

  public fillForm(account: AccountDto): void {
    this.accountForm.reset();
    this.accountForm.patchValue({
      username: account.username,
      email: account.email,
      full_name: account.full_name,
      language: account.language,
      role: account.role,
      accountStatus: account.is_active ? 'active' : 'disabled',
    });
  }

  public doUpdateUser(): void {
    if (!this.account) {
      return;
    }

    if (!this.validateFillForm()) {
      return;
    }

    this._accountService.update(this.account.id, this._updateAccount)
      .subscribe({
        next: (result: UpdateAccountResponseDto) => {
          const updatedAccount: SerializedAccountDto = {
            ...(this.account as SerializedAccountDto),
            ...(result.data ?? {}),
          };

          this._accountService.setAccountById(this.account!.id, updatedAccount);

          if (this.isCurrentAccount) {
            this._accountService.setAccount(updatedAccount);
          }

          this._messageService.add({
            severity: 'success',
            summary: this._translateService.instant('common.title.changes-successful'),
            detail: this._translateService.instant('common.messages.success.user-updated'),
          });

          this.updated.emit();
        },
        error: (error) => {
          console.error('Error updating account:', error);

          this._messageService.add({
            severity: 'error',
            summary: this._translateService.instant('common.title.update-failed'),
            detail: this._translateService.instant('common.messages.error.error-during-update'),
            life: 5000,
          });
        },
      });
  }

  public validateFillForm(): boolean {
    this._updateAccount = {};

    const email: string = this.accountForm.get('email')
      ?.value
      ?.trim() ?? '';
    const full_name: string = this.accountForm.get('full_name')
      ?.value
      ?.trim() ?? '';
    const language: string = this.accountForm.get('language')?.value ?? '';
    const role: string = this.accountForm.get('role')?.value ?? '';
    const currentPassword: string = this.accountForm.get('currentPassword')?.value ?? '';
    const newPassword: string = this.accountForm.get('password')?.value ?? '';
    const newPasswordRepeat: string = this.accountForm.get('passwordRepeat')?.value ?? '';
    const accountStatus: boolean = this.accountForm.get('accountStatus')?.value == 'active';

    if (email !== '' && email !== this.account?.email) {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

      if (!emailRegex.test(email)) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.meassages.error.valid-email-required'),
          life: 5000,
        });

        return false;
      }

      this._updateAccount.email = email;
    }

    if (full_name !== '' && full_name !== this.account?.full_name) {
      this._updateAccount.full_name = full_name;
    }

    if (this.account?.language !== language) {
      this._updateAccount.language = language;
    }

    if (!this.isCurrentAccount && role !== '' && this.account?.role !== role) {
      this._updateAccount.role = role as AccountRole;
    }

    const anyPasswordFilled: boolean =
      currentPassword !== '' || newPassword !== '' || newPasswordRepeat !== '';

    if (anyPasswordFilled) {
      if (this.isCurrentAccount && currentPassword === '') {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.messages.error.current-password-required'),
          life: 5000,
        });

        return false;
      }

      if (newPassword === '' || newPasswordRepeat === '') {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.messages.error.enter-new-password-twice'),
          life: 5000,
        });

        return false;
      }

      if (newPassword !== newPasswordRepeat) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.messages.error.not-matching-passwords'),
          life: 5000,
        });

        return false;
      }

      if (newPassword.length < 5) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.messages.error.new-password-minimum-length'),
          life: 5000,
        });

        return false;
      }

      if (this.isCurrentAccount) {
        this._updateAccount.password = currentPassword;
      }

      this._updateAccount.new_password = newPassword;
    }

    if (this.account?.is_active !== accountStatus) {
      this._updateAccount.is_active = accountStatus;
    }

    if (Object.keys(this._updateAccount).length === 0) {
      this._messageService.add({
        severity: 'info',
        summary: this._translateService.instant('common.title.info.no-changes'),
        detail: this._translateService.instant('common.messages.info.no-changes'),
        life: 3000,
      });
      return false;
    }
    return true;
  }
}
